
import { UserPreferences } from '@/features/settings/types';
import {
  Component, PropSync, Ref, Vue, Watch,
} from 'vue-property-decorator';
import { InProgress, SignatureType } from '../../types';

type ReturnOption = 'labelFree' | 'boxFree' | 'standard';

@Component
export default class cpcReturnsHint extends Vue {
  @Ref('returnSelect') returnSelect!: any;

  @PropSync('isLabelFree', { default: false }) isLabelFreeSync!: boolean;

  @PropSync('isStandardReturn', { default: false }) isStandardReturnSync!: boolean;

  @PropSync('isLabelFreeBoxFree', { default: false }) isLabelFreeBoxFreeSync!: boolean;

  @PropSync('isRequirementsMet', { default: false }) isRequirementsMetSync!: boolean;

  private returnOptions: Array<{ text: string, value: ReturnOption }> = [
    {
      text: this.$t('shipment.extrasShipment.labels.labelStandardReturn') as string,
      value: 'standard',
    },
    {
      text: this.$t('shipment.extrasShipment.labels.labelFreeReturn') as string,
      value: 'labelFree',
    },
    {
      text: this.$t('shipment.extrasShipment.labels.labelFreeAndBoxFreeReturn') as string,
      value: 'boxFree',
    },
  ];

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get returnOption(): ReturnOption {
    if (this.isLabelFreeBoxFreeSync) return 'boxFree';
    if (this.isLabelFreeSync) return 'labelFree';
    if (this.isStandardReturnSync) return 'standard';
    return 'standard';
  }

  get returnHints() {
    const {
      fromAddress, toAddress, packages, signatureOptions, temporaryPackage,
    } = this.inProgressData || {};
    const { defaultAddress } = this.userPreferences.shippingSettings || {};
    const { weight, weightUnits, insurance } = packages[0] || {};
    const {
      weight: tempWeight,
      weightUnits: tempWeightUnits,
      insurance: tempInsurance,
    } = temporaryPackage || {};

    const targetWeight = weight || tempWeight;
    const targetWeightUnits = weightUnits || tempWeightUnits;
    const targetInsurance = insurance ?? tempInsurance;

    return [
      {
        text: targetWeightUnits === 'Lbs'
          ? this.$t('shipment.extrasShipment.cpcReturnHints.items.weightLessThanLbs')
          : this.$t('shipment.extrasShipment.cpcReturnHints.items.weightLessThanKgs'),
        status: (targetWeightUnits === 'Lbs' && targetWeight && targetWeight <= 50)
          || (targetWeightUnits === 'Kgs' && targetWeight && targetWeight <= 22.7),
      },
      {
        text: this.$t('shipment.extrasShipment.cpcReturnHints.items.declaredValue'),
        status: !targetInsurance || targetInsurance <= 100,
      },
      {
        text: this.$t('shipment.extrasShipment.cpcReturnHints.items.withinCanada'),
        status: fromAddress.countryCode === 'CA' && toAddress.countryCode === 'CA',
      },
      {
        text: this.$t('shipment.extrasShipment.cpcReturnHints.items.yourAddress'),
        status: toAddress.postalCode === defaultAddress?.postalCode,
      },
      {
        text: this.$t('shipment.extrasShipment.cpcReturnHints.items.signatureRequirement'),
        status: signatureOptions === SignatureType.SignatureRequired,
      },
    ];
  }

  get isShowCPCRequirements() {
    return this.isLabelFreeSync || this.isLabelFreeBoxFreeSync;
  }

  get addressFingerprint(): string {
    const { toAddress, fromAddress } = this.inProgressData || {};
    return `${fromAddress?.countryCode}:${fromAddress?.postalCode}|${toAddress?.countryCode}:${toAddress?.postalCode}`;
  }

  @Watch('addressFingerprint')
  private onAddressChange() {
    const { shippingSettings } = this.userPreferences || {};
    const { defaultAddress } = shippingSettings || {};

    const { toAddress } = this.inProgressData || {};

    if (toAddress.postalCode === defaultAddress?.postalCode) return;

    this.isStandardReturnSync = false;
    this.isLabelFreeSync = false;
    this.isLabelFreeBoxFreeSync = false;
    this.isRequirementsMetSync = false;

    setTimeout(() => {
      this.$emit('updated');
    }, 800);
  }

  @Watch('returnHints', { deep: true, immediate: true })
  private onReturnHintsUpdate() {
    if (this.isStandardReturnSync) {
      this.isRequirementsMetSync = true;
      return;
    }
    this.isRequirementsMetSync = this.returnHints.every((hint) => hint.status);
  }

  private closeSelectMenu(): void {
    if (this.returnSelect) {
      this.returnSelect.blur();
    }
  }

  private handleReturnOptionsUpdate(value: ReturnOption, delay = 300) {
    this.isStandardReturnSync = value === 'standard';
    this.isLabelFreeSync = value === 'labelFree';
    this.isLabelFreeBoxFreeSync = value === 'boxFree';
    setTimeout(() => {
      this.$emit('updated');
    }, delay);
  }
}
